import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react'
import Cookies from 'js-cookie'
import { checkRiftboundLimit } from './utils'

const CartContext = createContext(null)

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      return Cookies.get('cart') ? JSON.parse(Cookies.get('cart')) : {}
    } catch {
      return {}
    }
  })

  const [notification, setNotification] = useState(null)
  const notifyTimer = useRef(null)

  const showNotification = useCallback((msg) => {
    setNotification(msg)
    if (notifyTimer.current) clearTimeout(notifyTimer.current)
    notifyTimer.current = setTimeout(() => setNotification(null), 3000)
  }, [])

  const dismissNotification = useCallback(() => {
    setNotification(null)
    if (notifyTimer.current) clearTimeout(notifyTimer.current)
  }, [])

  const addToCart = useCallback((id, productName) => {
    const currentQty = cartItems[id] || 0
    const error = checkRiftboundLimit(id, currentQty, 1)
    if (error) {
      showNotification(error)
      return
    }
    setCartItems((prev) => {
      const next = { ...prev, [id]: (prev[id] || 0) + 1 }
      Cookies.set('cart', JSON.stringify(next), { expires: 30 })
      return next
    })
    showNotification(productName ? `${productName} added to cart` : 'Added to cart')
  }, [showNotification, cartItems])

  const removeFromCart = useCallback((id) => {
    setCartItems((prev) => {
      const next = { ...prev }
      delete next[id]
      Cookies.set('cart', JSON.stringify(next), { expires: 30 })
      return next
    })
  }, [])

  const updateQuantity = useCallback((id, quantity) => {
    if (quantity < 1) return
    const error = checkRiftboundLimit(id, 0, quantity)
    if (error) {
      showNotification(error)
      return
    }
    setCartItems((prev) => {
      const next = { ...prev, [id]: quantity }
      Cookies.set('cart', JSON.stringify(next), { expires: 30 })
      return next
    })
  }, [showNotification])

  const clearCart = useCallback(() => {
    setCartItems({})
    Cookies.set('cart', JSON.stringify({}), { expires: 30 })
  }, [])

  const cartCount = useMemo(
    () => Object.values(cartItems).reduce((sum, qty) => sum + qty, 0),
    [cartItems],
  )

  const value = useMemo(
    () => ({ cartItems, cartCount, addToCart, removeFromCart, updateQuantity, clearCart, notification, dismissNotification }),
    [cartItems, cartCount, addToCart, removeFromCart, updateQuantity, clearCart, notification, dismissNotification],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = () => {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within a CartProvider')
  return ctx
}
